import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchProject, fetchProjectTasks } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import { formatTimestamp } from '../../hooks/useTimeAgo'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import GanttTimeline from '../../components/GanttTimeline'
import { usePersonaPopup } from '../../components/PersonaPopup'
import { useToast } from '../../components/Toast'
import type { ProjectTask } from '../../lib/types'

const STATUS_COLORS: Record<string, string> = {
  pending: '#6b7280', in_progress: '#3b82f6', completed: '#3fb950',
  blocked: '#f85149', deferred: '#8b5cf6', review: '#d29922',
}

function TaskRow({ task, projectId }: { task: ProjectTask; projectId: string }) {
  return (
    <Link
      to={`/project/${projectId}/task/${task.id}`}
      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[var(--bg-elevated)] no-underline transition-colors"
    >
      <div
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: STATUS_COLORS[task.status] || '#6b7280' }}
      />
      <span className="text-sm flex-1 truncate" style={{ color: 'var(--text-primary)' }}>
        {task.title}
      </span>
      {task.assignee && <PersonaAvatar name={task.assignee} size={20} />}
      <span className="text-xs capitalize" style={{ color: STATUS_COLORS[task.status] || 'var(--text-muted)' }}>
        {task.status.replace('_', ' ')}
      </span>
    </Link>
  )
}

const TABS = ['tasks', 'timeline', 'thread', 'artifacts'] as const

function PhaseSection({ phase, tasks, projectId, defaultOpen }: { phase: string; tasks: ProjectTask[]; projectId: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? false)
  const completed = tasks.filter(t => t.status === 'completed').length
  const blocked = tasks.filter(t => t.status === 'blocked').length

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors hover:bg-[var(--bg-elevated)]"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <span className="text-xs" style={{ color: 'var(--text-muted)', width: '1em', textAlign: 'center' }}>
          {open ? '▼' : '▶'}
        </span>
        <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary)' }}>
          {phase}
        </span>
        <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: 'var(--text-muted)' }}>
          {completed}/{tasks.length}
        </span>
        {blocked > 0 && (
          <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: '#f85149' }}>
            {blocked} blocked
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 mt-1 ml-3">
          {tasks.map(t => <TaskRow key={t.id} task={t} projectId={projectId} />)}
        </div>
      )}
    </div>
  )
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hoverProps } = usePersonaPopup()
  const toast = useToast()
  const [tab, setTab] = useState<string>('tasks')
  const [threadDraft, setThreadDraft] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const queryClient = useQueryClient()

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  const postToThread = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/projects/${id}/thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ author: 'joel', content }),
      })
      if (!res.ok) throw new Error('Failed')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      setThreadDraft('')
    },
  })

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id!),
    enabled: !!id,
  })

  const { data: tasksData } = useQuery({
    queryKey: ['projectTasks', id],
    queryFn: () => fetchProjectTasks(id!),
    enabled: !!id,
  })

  const tasks = tasksData?.tasks || []

  async function handleChat() {
    if (!id || chatLoading) return
    setChatLoading(true)
    try {
      // Step 1: look for an existing project conversation (authoritative server-side lookup).
      try {
        const res = await fetch(`/api/projects/${id}/conversations`, { headers: getAuthHeaders() })
        if (res.ok) {
          const existing = await res.json()
          if (Array.isArray(existing) && existing.length > 0) {
            navigate(`/conversation/${existing[0].id}`)
            return
          }
        } else {
          const body = await res.text()
          throw new Error(`lookup ${res.status}: ${body.slice(0, 200)}`)
        }
      } catch (e) {
        toast(`Couldn't look up project chat: ${e instanceof Error ? e.message : 'unknown'}`, 'error')
        return
      }

      // Step 2: no existing — create via find_or_create so the backend dedupes authoritatively.
      const people = new Set<string>()
      if (project?.lead) people.add(project.lead)
      project?.stakeholders?.forEach((s: string) => people.add(s))
      tasks.forEach((t: ProjectTask) => { if (t.assignee) people.add(t.assignee) })
      const participants = ['joel', ...[...people].filter(p => p !== 'joel')]
      const res = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          participants,
          title: (project?.title || 'Project') + ' — Project Chat',
          type: 'group',
          project_id: id,
          find_or_create: true,
        }),
      })
      if (!res.ok) {
        const body = await res.text()
        let detail = body.slice(0, 300)
        try { const j = JSON.parse(body); if (j.error) detail = j.error + (j.participant ? ` (${j.participant})` : '') } catch {}
        toast(`Couldn't open project chat: ${detail}`, 'error')
        return
      }
      const data = await res.json()
      if (data.id) {
        navigate(`/conversation/${data.id}`)
      } else {
        toast('Project chat response missing conversation id', 'error')
      }
    } finally {
      setChatLoading(false)
    }
  }

  if (isLoading) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
  }
  if (!project) {
    return <div className="p-6 text-sm" style={{ color: 'var(--danger)' }}>Project not found</div>
  }

  const completed = tasks.filter(t => t.status === 'completed').length

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link to="/projects" className="text-xs mb-2 inline-block no-underline" style={{ color: 'var(--text-muted)' }}>
          ← Projects
        </Link>
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{project.title}</h1>
        <div className="flex items-center gap-3 mt-2">
          {project.lead && (
            <div className="flex items-center gap-1.5">
              <PersonaAvatar name={project.lead} size={20} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Lead</span>
            </div>
          )}
          {project.stakeholders?.map(s => (
            <PersonaAvatar key={s} name={s} size={20} />
          ))}
          <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{
            background: project.status === 'active' ? 'rgba(63,185,80,0.15)' : 'var(--bg-elevated)',
            color: project.status === 'active' ? 'var(--success)' : 'var(--text-muted)',
          }}>
            {project.status}
          </span>
          {tasks.length > 0 && (
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {completed}/{tasks.length} tasks done
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={handleChat} disabled={chatLoading} className="text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50" style={{ background: 'var(--bg-elevated)', border: '1px solid #58a6ff', color: '#58a6ff', cursor: chatLoading ? 'wait' : 'pointer' }}>
            {chatLoading ? '⏳ Opening…' : '💬 Chat'}
          </button>
          {project.status === 'active' && completed === tasks.length && tasks.length > 0 && (
            <button
              onClick={async () => {
                if (!confirm('Mark this project as completed?')) return
                const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
                await fetch(`/api/projects/${id}/status`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                  body: JSON.stringify({ status: 'completed' }),
                })
                queryClient.invalidateQueries({ queryKey: ['project', id] })
              }}
              className="text-xs px-3 py-1.5 rounded font-medium"
              style={{ background: 'var(--bg-elevated)', border: '1px solid #3fb950', color: '#3fb950', cursor: 'pointer' }}
            >
              ✓ Complete Project
            </button>
          )}
        </div>
        {project.description && (
          <div className="mt-3">
            <MarkdownRenderer content={project.description} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4" style={{ borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="text-xs px-3 py-2 capitalize transition-colors"
            style={{
              color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
              borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            }}
          >
            {t} {t === 'tasks' && tasks.length > 0 ? `(${tasks.length})` : ''}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'tasks' && (() => {
        if (tasks.length === 0) {
          return <p className="text-xs py-4" style={{ color: 'var(--text-muted)' }}>No tasks</p>
        }
        const hasPhases = tasks.some(t => t.phase)
        if (!hasPhases) {
          return (
            <div className="flex flex-col gap-0.5">
              {tasks.map(t => <TaskRow key={t.id} task={t} projectId={id!} />)}
            </div>
          )
        }
        const phaseOrder: string[] = []
        const phaseMap: Record<string, ProjectTask[]> = {}
        for (const t of tasks) {
          const p = t.phase || 'Unphased'
          if (!phaseMap[p]) { phaseMap[p] = []; phaseOrder.push(p) }
          phaseMap[p].push(t)
        }
        return (
          <div className="flex flex-col gap-1">
            {phaseOrder.map((p, i) => (
              <PhaseSection key={p} phase={p} tasks={phaseMap[p]} projectId={id!} defaultOpen={i === 0} />
            ))}
          </div>
        )
      })()}

      {tab === 'timeline' && (
        <GanttTimeline tasks={tasks} projectId={id!} />
      )}

      {tab === 'thread' && (
        <div className="flex flex-col gap-2">
          {(project.thread || []).map(msg => (
            <div key={msg.id} className="flex gap-2 py-2">
              <PersonaAvatar name={msg.author} size={24} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium" {...hoverProps(msg.author)} style={{ cursor: 'pointer', color: 'var(--text-primary)' }}>{msg.author}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatTimestamp(msg.timestamp)}
                  </span>
                </div>
                <MarkdownRenderer content={msg.content} />
              </div>
            </div>
          ))}
          {(!project.thread || project.thread.length === 0) && (
            <p className="text-xs py-4" style={{ color: 'var(--text-muted)' }}>No thread messages</p>
          )}
          {/* Compose */}
          <div className="mt-3 flex gap-2">
            <textarea
              value={threadDraft}
              onChange={e => setThreadDraft(e.target.value)}
              placeholder="Post to thread..."
              rows={2}
              className="flex-1 px-3 py-2 rounded text-xs outline-none resize-none"
              style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && threadDraft.trim()) { e.preventDefault(); postToThread.mutate(threadDraft.trim()) } }}
            />
            <button
              onClick={() => { if (threadDraft.trim()) postToThread.mutate(threadDraft.trim()) }}
              disabled={!threadDraft.trim() || postToThread.isPending}
              className="self-end text-xs px-3 py-1.5 rounded disabled:opacity-50 shrink-0"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              Post
            </button>
          </div>
        </div>
      )}

      {tab === 'artifacts' && (
        <div className="flex flex-col gap-2">
          {(project.artifacts || []).map(a => (
            <div key={a.id} className="p-3 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{a.description || a.path || a.id}</div>
              {a.type && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.type}</span>}
            </div>
          ))}
          {(!project.artifacts || project.artifacts.length === 0) && (
            <p className="text-xs py-4" style={{ color: 'var(--text-muted)' }}>No artifacts</p>
          )}
        </div>
      )}
    </div>
  )
}
